import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { ClientSortingFields, ClientsFiltersProps, ListClientModel, Order } from '../models';

interface IClientsContext {
  clientsUpdate: boolean;
  toggleClientsUpdate: () => void;
  clients: ListClientModel[];
  setClients: (val: ListClientModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: ClientSortingFields;
  setOrderBy: (val: ClientSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: ClientSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  filters: ClientsFiltersProps;
  setFilters: (val: ClientsFiltersProps) => void;
  resetFilters: () => void;
}

export const ClientsContext = createContext<IClientsContext>({} as IClientsContext);

export const ClientsProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [clientsUpdate, setClientsUpdate] = useState(false);
  const toggleUpdate = (): void => setClientsUpdate(!clientsUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<ClientSortingFields>(toggleUpdate, 'name', 'asc');

  const [clients, setClients] = useState<ListClientModel[]>([]);
  const [loading, setLoading] = useState(false);
  const emptyFilters = {
    leadBrewerId: undefined,
    status: undefined,
    segment: undefined
  };
  const [filters, setFilters] = useState<ClientsFiltersProps>(emptyFilters);
  const resetFilters = (): void => {
    setFilters(emptyFilters);
    setPage(0);
    setLimit(20);
  };

  return (
    <ClientsContext.Provider
      value={{
        filters,
        setFilters,
        resetFilters,
        clientsUpdate,
        toggleClientsUpdate: toggleUpdate,
        clients,
        setClients,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};
